import { useMemo } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { AppShell } from '@/components/layout/AppShell';
import { ItemRibbon } from '@/components/layout/ItemRibbon';
import { LevelTabStrip } from '@/components/rubric/LevelTabStrip';
import { IndicatorColumn } from '@/components/rubric/IndicatorColumn';
import { ItemFooterBar } from '@/components/rubric/ItemFooterBar';
import { ItemNaBanner } from '@/components/rubric/ItemNaBanner';
import { ItemNotes } from '@/components/rubric/ItemNotes';
import {
  DEFAULT_INSTRUMENT_ID,
  SCORED_LEVELS,
  getItem,
  indicatorsByLevel,
  itemsInOrder,
} from '@/lib/schema/loader';
import { useObservation } from '@/lib/state/useObservation';
import { scoreItem, levelsFullyYes } from '@/lib/scoring/itemScore';

export function ItemView() {
  const { itemId } = useParams<{ itemId: string }>();
  const { state, completeItem } = useObservation();

  const item = itemId ? getItem(itemId) : undefined;

  const scoreResult = useMemo(() => {
    if (!item) return null;
    return scoreItem(item, state.responses, {
      itemNa: state.itemNa[item.id] ?? false,
    });
  }, [item, state.responses, state.itemNa]);

  const grouped = useMemo(
    () => (item ? indicatorsByLevel(item) : null),
    [item],
  );

  const fullyYesMap = useMemo(
    () =>
      item
        ? levelsFullyYes(item, state.responses)
        : { 1: false, 3: false, 5: false, 7: false },
    [item, state.responses],
  );

  if (!item || !grouped || !scoreResult) {
    const first = itemsInOrder(DEFAULT_INSTRUMENT_ID)[0];
    if (!first) return null;
    return (
      <Navigate
        to={`/observation/${state.id}/items/${first.id}`}
        replace
      />
    );
  }

  const itemStatus = state.itemStatus[item.id] ?? 'untouched';

  return (
    <AppShell instrumentId={item.instrument_id}>
      <ItemRibbon itemId={item.id} />
      <ItemNaBanner item={item} />
      <LevelTabStrip currentScore={scoreResult.score} />
      <div
        className={
          'grid grid-cols-1 gap-3 p-6 md:grid-cols-2 xl:grid-cols-4' +
          (scoreResult.itemNa
            ? ' pointer-events-none select-none opacity-40'
            : '')
        }
        aria-disabled={scoreResult.itemNa}
      >
        {SCORED_LEVELS.map((level) => (
          <IndicatorColumn
            key={level}
            itemId={item.id}
            level={level}
            indicators={grouped[level]}
            fullyYes={fullyYesMap[level]}
          />
        ))}
      </div>
      <ItemNotes itemId={item.id} />
      <ItemFooterBar
        item={item}
        canComplete={
          scoreResult.itemNa ||
          (scoreResult.complete && scoreResult.fullySwept)
        }
        isComplete={itemStatus === 'complete'}
        onComplete={() => completeItem(item.id)}
        scoreResult={scoreResult}
      />
    </AppShell>
  );
}
